import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, ArrowRight, ArrowLeft } from "lucide-react";
import Reveal from "@/components/Reveal";
import { BLOG_POSTS } from "@/data/content";

export default function BlogPostDetail() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  if (!post) return <Navigate to="/blog" replace />;

  const related = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 3);
  const initials = post.author.split(" ").map((n) => n[0]).join("");

  const renderBlock = (block, i) => {
    if (block.type === "h2") {
      return (
        <h2 key={i} className="display-h3 mt-14 mb-6">
          {block.text}
        </h2>
      );
    }
    if (block.type === "list") {
      return (
        <ul key={i} className="my-8 space-y-4">
          {block.items.map((item, j) => (
            <li key={j} className="flex items-start gap-4">
              <span className="text-clay-400 mt-1.5 shrink-0">—</span>
              <span className="text-bone-100/90 leading-relaxed">{item}</span>
            </li>
          ))}
        </ul>
      );
    }
    return (
      <p
        key={i}
        className="text-lg leading-relaxed text-bone-200/85 mb-6 font-light"
        style={{ fontSize: "1.15rem", lineHeight: "1.75" }}
      >
        {block.text}
      </p>
    );
  };

  return (
    <div className="relative">
      {/* HEADER */}
      <section className="relative pt-16 md:pt-24 pb-12">
        <div className="container-x max-w-4xl">
          <Reveal>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest2 text-bone-200/60 hover:text-clay-400 transition-colors mb-12"
              data-testid="blog-detail-back"
            >
              <ArrowLeft size={14} />
              All writing
            </Link>
          </Reveal>

          <Reveal delay={40}>
            <div className="flex items-center gap-4 mb-8">
              <span className="text-xs uppercase tracking-widest2 text-clay-400">
                {post.category}
              </span>
              <span className="w-1 h-1 rounded-full bg-bone-200/30" />
              <span className="text-xs uppercase tracking-widest2 text-bone-200/50">
                {post.date}
              </span>
              <span className="w-1 h-1 rounded-full bg-bone-200/30" />
              <span className="text-xs uppercase tracking-widest2 text-bone-200/50">
                {post.readTime}
              </span>
            </div>
          </Reveal>

          <Reveal delay={80}>
            <h1
              className="display-h1"
              data-testid={`blog-detail-title-${post.slug}`}
            >
              {post.title}
            </h1>
          </Reveal>

          <Reveal delay={160}>
            <div className="mt-12 flex items-center gap-4 pb-8 border-b border-white/10">
              <div className="w-12 h-12 rounded-full bg-clay-500/20 border border-clay-400/40 flex items-center justify-center font-display text-clay-400">
                {initials}
              </div>
              <div>
                <div className="text-bone-100 font-semibold">{post.author}</div>
                <div className="text-xs uppercase tracking-widest2 text-bone-200/50">
                  {post.authorRole}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* COVER IMAGE */}
      <section className="pb-16">
        <div className="container-x max-w-4xl">
          <Reveal>
            <div className="relative aspect-[16/9] rounded-lg overflow-hidden border border-white/10">
              <img src={post.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-950/60 via-transparent to-transparent" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* BODY */}
      <section className="pb-24 md:pb-32">
        <div className="container-x max-w-3xl">
          <Reveal>
            <article className="prose-invert">
              {post.body.map((block, i) => renderBlock(block, i))}
            </article>
          </Reveal>

          <Reveal delay={100}>
            <div className="mt-16 pt-10 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <p className="text-sm text-bone-200/60 max-w-md">
                Enjoyed this? The RevGrow briefing lands in the inboxes of
                CROs and CMOs every month. One essay, one framework, one teardown.
              </p>
              <Link to="/contact" className="btn-primary shrink-0">
                Subscribe
                <ArrowUpRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* RELATED */}
      <section className="py-24 md:py-32 bg-ink-900/40 border-y border-white/5">
        <div className="container-x">
          <div className="mb-14">
            <Reveal><p className="eyebrow mb-6">More field notes</p></Reveal>
            <Reveal delay={80}>
              <h2 className="display-h2">Keep reading.</h2>
            </Reveal>
          </div>

          <ul className="border-t border-white/10">
            {related.map((p, i) => (
              <Reveal key={p.slug} delay={i * 60}>
                <li>
                  <Link
                    to={`/blog/${p.slug}`}
                    data-testid={`blog-detail-related-${p.slug}`}
                    className="block border-b border-white/10 py-8 md:py-10 group grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 transition-colors hover:bg-white/[0.02]"
                  >
                    <div className="md:col-span-3 space-y-2">
                      <div className="text-xs uppercase tracking-widest2 text-clay-400">
                        {p.category}
                      </div>
                      <div className="text-xs uppercase tracking-widest2 text-bone-200/50">
                        {p.date} · {p.readTime}
                      </div>
                    </div>
                    <div className="md:col-span-8">
                      <h3 className="display-h3 mb-3 transition-colors group-hover:text-clay-400">
                        {p.title}
                      </h3>
                      <p className="text-sm text-bone-200/70 leading-relaxed">
                        {p.excerpt}
                      </p>
                    </div>
                    <div className="md:col-span-1 md:flex md:items-start md:justify-end">
                      <ArrowUpRight
                        size={20}
                        className="text-bone-100/40 transition-all duration-500 group-hover:text-clay-400 group-hover:-translate-y-1 group-hover:translate-x-1"
                      />
                    </div>
                  </Link>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
